/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Pagination Component
 * -----------
 * A reusable pagination control that shows a fixed-size range (window) of page numbers at a time.
 * Useful for navigating through large sets of pages without cluttering the UI.
 * This approach keeps UI compact, scalable, and user-friendly for large pagination sets.
 * Props:
 * - totalPages (number): Total number of pages available.
 * - currentPage (number): The currently active page.
 * - onPageChange (function): Callback function invoked with new page number when user navigates.
 * - windowSize (number, optional): How many page numbers to display at once (default 10).
 * - theme (object): Theme colors used for styling pagination buttons.
 */

import React, { useState, useEffect } from 'react';

const Pagination = ({ totalPages, currentPage, onPageChange, windowSize = 10, theme }) => {
  const [windowStart, setWindowStart] = useState(1);

  const windowEnd = Math.min(windowStart + windowSize - 1, totalPages);

  useEffect(() => {
    if (currentPage < windowStart) {
      setWindowStart(currentPage);
    } else if (currentPage > windowEnd) {
      setWindowStart(currentPage - windowSize + 1);
    }
  }, [currentPage, windowStart, windowEnd, windowSize]);

  const prevWindow = () => {
    setWindowStart(Math.max(1, windowStart - windowSize));
  };

  const nextWindow = () => {
    setWindowStart(Math.min(totalPages - windowSize + 1, windowStart + windowSize));
  };

  const pages = [];
  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i);
  }

  return (
    <nav aria-label="Pagination navigation" className="mt-4">
      <ul className="pagination justify-content-center" style={{ cursor: 'pointer' }}>
        <li
          className={`page-item${currentPage === 1 ? ' disabled' : ''}`}
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          style={{ userSelect: 'none' }}
        >
          <span
            className="page-link"
            style={{ color: currentPage === 1 ? 'gray' : theme.primary }}
          >
            {'<'}
          </span>
        </li>

        {windowStart > 1 && (
          <li className="page-item" onClick={prevWindow}>
            <span className="page-link" style={{ color: theme.primary, cursor: 'pointer' }}>
              ...
            </span>
          </li>
        )}

        {pages.map(page => (
          <li
            key={page}
            className={`page-item${page === currentPage ? ' active' : ''}`}
            onClick={() => onPageChange(page)}
          >
            <span
              className="page-link"
              style={{
                color: page === currentPage ? '#fff' : theme.primary,
                backgroundColor: page === currentPage ? theme.primary : 'transparent',
                borderColor: theme.primary,
              }}
            >
              {page}
            </span>
          </li>
        ))}

        {windowEnd < totalPages && (
          <li className="page-item" onClick={nextWindow}>
            <span className="page-link" style={{ color: theme.primary, cursor: 'pointer' }}>
              ...
            </span>
          </li>
        )}

        <li
          className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}
          onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
          style={{ userSelect: 'none' }}
        >
          <span
            className="page-link"
            style={{ color: currentPage === totalPages ? 'gray' : theme.primary }}
          >
            {'>'}
          </span>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
